import { useState, useEffect, FormEvent } from 'react';
import {
  resetPassword,
  confirmResetPassword,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { useNavigate, Link } from 'react-router-dom';
import './Auth.css';

// ✅ Ensure Amplify is configured once (in AmplifyClient)
import '../../lib/AmplifyClient';

export default function ForgotPasswordComponent() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Redirect if already logged in
  useEffect(() => {
    fetchUserAttributes()
      .then(() => navigate('/'))
      .catch(() => {});
  }, [navigate]);

  function getFriendlyError(err: any): string {
    if (!err?.name) return err?.message || 'An unknown error occurred';
    switch (err.name) {
      case 'UserNotFoundException':
        return 'No user found with that email.';
      case 'CodeMismatchException':
        return 'The code you entered is incorrect.';
      case 'ExpiredCodeException':
        return 'The reset code has expired.';
      default:
        return err.message || 'Something went wrong.';
    }
  }

  async function handleRequestReset(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    try {
      await resetPassword({ username: email });
      setStep('confirm');
      setMessage('Code sent! Check your email.');
    } catch (err: any) {
      setError(getFriendlyError(err));
    }
  }

  async function handleConfirmReset(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      setMessage('Password reset! Redirecting to login...');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: any) {
      setError(getFriendlyError(err));
    }
  }

  return (
    <div className="auth-container">
      <h2>Reset Password</h2>

      {step === 'request' ? (
        <form onSubmit={handleRequestReset}>
          <input
            type="text"
            name="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit">Send Reset Code</button>
        </form>
      ) : (
        <form onSubmit={handleConfirmReset}>
          <p>
            Enter the code sent to <strong>{email}</strong> and your new password.
          </p>
          <input
            type="text"
            placeholder="Confirmation Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <button type="submit">Confirm Reset</button>
        </form>
      )}

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <div className="auth-footer">
        <Link to="/login">Back to login</Link>
      </div>
    </div>
  );
}
